/**
 * Attaching a document before the conversation exists.
 *
 * A chat document's S3 key is built server-side from the conversation it belongs
 * to — `tenants/…/conversations/{conversationId}/documents/{documentId}.{ext}` —
 * but on the new-chat screen there is no conversation yet: the AI service only
 * assigns one when the first message is sent. So the first document is uploaded
 * under a client-generated *draft* conversation folder, and the resulting key
 * travels with the message, which is all the AI service needs to read the file.
 *
 * The key is persisted alongside the message (`message_files[].s3_key`), so the
 * draft folder stays discoverable afterwards — see
 * {@link resolveDocumentConversationId}, which is what keeps downloads working
 * once the real conversation id exists and no longer matches the folder.
 */

/** Marks an S3 folder created before its conversation existed. */
const DRAFT_CONVERSATION_PREFIX = 'draft-';

/** Reads the conversation segment out of a chat document's S3 key. */
const CONVERSATION_SEGMENT = /\/conversations\/([^/]+)\/documents\//;

/**
 * A one-off folder id for documents attached on the new-chat screen. Prefixed so
 * a draft folder is recognisable in storage and in logs.
 */
export const createDraftConversationId = (): string =>
  `${DRAFT_CONVERSATION_PREFIX}${crypto.randomUUID()}`;

/**
 * The conversation id a document is actually filed under.
 *
 * Download and delete URLs are rebuilt from a conversation id, so passing the
 * *current* conversation would miss any document uploaded before that
 * conversation existed. The stored key is the source of truth; the current id is
 * only the fallback for records saved without one.
 */
export const resolveDocumentConversationId = (
  s3Key: string | undefined,
  currentConversationId: string,
): string => s3Key?.match(CONVERSATION_SEGMENT)?.[1] ?? currentConversationId;
